<template>
  <div>
    <div
      v-if="showPermissionModal"
      class="custom-modal-overlay"
    >
      <div class="custom-modal-content permission-modal">
        <div class="modal-header">
          <h5 class="modal-title">
            Permissões necessárias
          </h5>
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="closeModal"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="permissions-container">
            <h5 class="text-center mb-4">
              Para marcar o ponto, precisamos das seguintes permissões:
            </h5>

            <div class="permission-item">
              <div
                class="permission-icon"
                :class="getPermissionIconClass('camera')"
              >
                <i class="fas fa-camera" />
              </div>
              <div class="permission-info">
                <h6>Câmera</h6>
                <p>{{ getPermissionMessage('camera') }}</p>
              </div>
              <div class="permission-action">
                <button
                  v-if="permissionStatus.camera !== 'granted'"
                  class="btn btn-sm btn-primary"
                  @click="requestCameraPermission"
                >
                  Permitir
                </button>
                <span
                  v-else
                  class="permission-granted"
                ><i class="fas fa-check" /></span>
              </div>
            </div>

            <div class="permission-item">
              <div
                class="permission-icon"
                :class="getPermissionIconClass('location')"
              >
                <i class="fas fa-map-marker-alt" />
              </div>
              <div class="permission-info">
                <h6>Localização</h6>
                <p>{{ getPermissionMessage('location') }}</p>
              </div>
              <div class="permission-action">
                <button
                  v-if="permissionStatus.location !== 'granted'"
                  class="btn btn-sm btn-primary"
                  @click="requestLocationPermission"
                >
                  Permitir
                </button>
                <span
                  v-else
                  class="permission-granted"
                ><i class="fas fa-check" /></span>
              </div>
            </div>

            <div
              v-if="isMobile && permissionStatus.location !== 'granted'"
              class="mobile-instructions"
            >
              <h6><i class="fas fa-info-circle text-info" /> Instruções para celular:</h6>
              <ol class="pl-3 mt-2">
                <li>Se o navegador não mostrar a opção para permitir localização, verifique se ela está ativada nas configurações do seu celular</li>
                <li>Acesse as configurações do seu celular</li>
                <li>Vá em "Privacidade" ou "Permissões"</li>
                <li>Selecione "Localização" e certifique-se de que está ativada</li>
                <li>Volte para o navegador e tente novamente</li>
              </ol>
              <div class="text-center mt-3">
                <button
                  class="btn btn-info btn-sm"
                  @click="forceLocationPermission"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer justify-content-center">
          <button
            v-if="allPermissionsGranted"
            class="btn btn-success"
            @click="continueToCamera"
          >
            Continuar
          </button>
          <button
            v-else
            class="btn btn-secondary"
            @click="closeModal"
          >
            Fechar
          </button>
          <a
            href="#"
            class="btn btn-link"
            @click.prevent="openBrowserSettings"
          >
            Abrir configurações do navegador
          </a>
        </div>
      </div>
    </div>

    <!-- Modal de instruções para localização em dispositivos móveis -->
    <div
      v-if="showMobileInstructions"
      class="custom-modal-overlay"
    >
      <div class="custom-modal-content permission-modal">
        <div class="modal-header">
          <h5 class="modal-title">
            Permissão de Localização
          </h5>
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="showMobileInstructions = false"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="mobile-instructions">
            <div class="text-center mb-3">
              <i
                class="fas fa-map-marker-alt text-danger"
                style="font-size: 3rem;"
              />
            </div>
            <h6 class="text-center mb-3">
              Como permitir acesso à localização no seu celular
            </h6>

            <div class="instruction-step">
              <div class="step-number">
                1
              </div>
              <div class="step-text">
                Saia do app e vá para as configurações do seu celular
              </div>
            </div>

            <div class="instruction-step">
              <div class="step-number">
                2
              </div>
              <div class="step-text">
                Procure por "Privacidade" ou "Permissões do Aplicativo"
              </div>
            </div>

            <div class="instruction-step">
              <div class="step-number">
                3
              </div>
              <div class="step-text">
                Toque em "Localização" e ative-a para o navegador que você está usando
              </div>
            </div>

            <div class="instruction-step">
              <div class="step-number">
                4
              </div>
              <div class="step-text">
                Volte para este aplicativo e tente marcar ponto novamente
              </div>
            </div>

            <div class="text-center mt-4">
              <button
                class="btn btn-primary"
                @click="showMobileInstructions = false; forceLocationPermission()"
              >
                Tentei ativar, verificar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PermissionManager',
  props: {
    showPermissionModal: {
      type: Boolean,
      default: false,
    },
    permissionStatus: {
      type: Object,
      required: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMobileInstructions: false,
      cameraPermission: null,
      locationPermission: null,
      location: null,
    }
  },
  computed: {
    allPermissionsGranted() {
      return this.permissionStatus.camera === 'granted'
             && this.permissionStatus.location === 'granted'
    },
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },

    continueToCamera() {
      this.$emit('continue', this.location)
    },

    // Verifica permissão de câmera
    async requestCameraPermission() {
      try {
        console.log('Solicitando permissão da câmera...')

        if (this.isMobile) {
          console.log('Dispositivo móvel detectado, tentando abordagem específica para móveis')
          const streams = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
          if (streams) streams.getTracks().forEach(track => track.stop())

          const constraints = {
            audio: false,
            video: {
              facingMode: 'user', // Câmera frontal
              width: { ideal: 640 },
              height: { ideal: 480 },
            },
          }

          try {
            await navigator.mediaDevices.getUserMedia(constraints)
          } catch (mobileError) {
            console.log('Primeira tentativa falhou, tentando novamente...')
            await new Promise(resolve => setTimeout(resolve, 500))
            await navigator.mediaDevices.getUserMedia(constraints)
          }
        } else {
          await navigator.mediaDevices.getUserMedia({ video: true })
        }

        console.log('Permissão da câmera concedida com sucesso!')
        this.cameraPermission = true
        this.$emit('permission-granted', 'camera')
        this.$message('Sucesso', 'Permissão para câmera concedida.', 'success')

        if (this.allPermissionsGranted) {
          setTimeout(() => {
            this.continueToCamera()
          }, 1000)
        }
      } catch (error) {
        console.error('Erro ao solicitar permissão da câmera:', error)
        this.$emit('permission-error', 'camera', error)
      }
    },

    async requestLocationPermission() {
      try {
        console.log('Solicitando permissão de localização...')

        if (this.isMobile) {
          console.log('Dispositivo móvel detectado, usando abordagem específica para localização em móveis')
          const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }

          try {
            await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, options)
            })
          } catch (mobileError) {
            console.log('Primeira tentativa de localização falhou, tentando novamente...', mobileError)

            if (mobileError.code === 1) { // PERMISSION_DENIED
              this.showMobileInstructions = true
              this.$emit('close')
              throw new Error('Permissão negada pelo usuário')
            }

            await new Promise(resolve => setTimeout(resolve, 500))
            await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: false,
                timeout: 15000,
                maximumAge: 0,
              })
            })
          }
        }

        // Se chegou aqui, obteve a localização com sucesso
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            pos => {
              resolve(pos)
            },
            error => {
              reject(error)
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
          )
        })

        // Salvar os dados de localização
        this.location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }

        // Atualizar status da permissão
        this.locationPermission = true
        this.$emit('permission-granted', 'location', this.location)
        this.$message('Sucesso', 'Permissão para localização concedida.', 'success')

        if (this.allPermissionsGranted) {
          setTimeout(() => {
            this.continueToCamera()
          }, 1000)
        }
      } catch (error) {
        console.error('Erro ao solicitar permissão de localização:', error)
        this.$emit('permission-error', 'location', error)
      }
    },

    async forceLocationPermission() {
      // Em dispositivos móveis, é melhor mostrar instruções específicas
      if (this.isMobile && this.permissionStatus.location !== 'granted') {
        this.showMobileInstructions = true
        this.$emit('close')
        return
      }

      try {
        // Tenta forçar uma nova solicitação de localização com alta precisão
        const result = await new Promise(resolve => {
          navigator.geolocation.getCurrentPosition(
            position => {
              const locationData = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              }
              resolve({ status: 'success', location: locationData })
            },
            error => {
              console.error('Erro na solicitação de localização:', error)
              resolve({ status: 'error', error })
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            },
          )
        })

        if (result.status === 'success') {
          this.location = result.location
          this.locationPermission = true
          this.$emit('permission-granted', 'location', this.location)
          this.$message('Sucesso', 'Permissão de localização concedida com sucesso!', 'success')

          // Verifica se todas as permissões foram concedidas
          if (this.allPermissionsGranted) {
            setTimeout(() => {
              this.continueToCamera()
            }, 1000)
          }
        } else {
          this.$message(
            'Atenção',
            'Não foi possível obter a localização. Por favor, verifique as configurações do seu celular.',
            'warning',
          )
        }
      } catch (error) {
        console.error('Erro ao forçar permissão de localização:', error)
        this.$message(
          'Erro',
          'Ocorreu um erro ao solicitar sua localização.',
          'error',
        )
      }
    },

    openBrowserSettings() {
      const url = this.getBrowserSettingsUrl()
      if (url) {
        window.open(url, '_blank')
      } else {
        this.$message('Atenção', 'Não foi possível abrir as configurações automaticamente. Verifique manualmente.', 'info')
      }
    },

    getBrowserSettingsUrl() {
      const ua = navigator.userAgent
      if (ua.includes('Chrome')) return 'chrome://settings/content/camera'
      if (ua.includes('Firefox')) return 'about:preferences#privacy'
      if (ua.includes('Safari')) return null // Safari não permite abrir diretamente
      return null
    },

    getPermissionIconClass(permission) {
      if (this.permissionStatus[permission] === 'granted') {
        return 'text-success'
      } if (this.permissionStatus[permission] === 'denied') {
        return 'text-danger'
      }
      return 'text-warning'
    },

    getPermissionMessage(permission) {
      switch (this.permissionStatus[permission]) {
        case 'granted':
          return 'Permissão concedida'
        case 'denied':
          return 'Permissão negada'
        case 'prompt':
          return 'Clique em "Permitir" para conceder acesso'
        default:
          return 'Status desconhecido'
      }
    },
  },
}
</script>

<style scoped>
/* Estilos para instruções móveis */
.mobile-instructions {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
  border: 1px solid #dee2e6;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  background-color: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.step-number {
  background-color: #007bff;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  flex-shrink: 0;
}

.step-text {
  flex: 1;
  line-height: 1.4;
}

/* Estilos adicionais para melhorar a visibilidade e usabilidade */
.permission-modal {
  max-width: 500px;
  width: 90%;
}

.permissions-container {
  margin-top: 10px;
}

.permission-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.permission-icon {
  font-size: 2rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 10px;
}

.permission-info {
  flex: 1;
}

.permission-action {
  display: flex;
  align-items: center;
}

.permission-granted {
  color: #28a745;
  font-weight: bold;
}

/* Correção dos estilos do modal para garantir centralização adequada */
.custom-modal-overlay {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 10px;
}

.custom-modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 700px;
  width: 95%;
  padding: 0;
  box-shadow: 0 2px 16px rgba(0,0,0,0.3);
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transform: translateY(0);
}

.custom-modal-content .modal-body {
  overflow-y: auto;
  padding: 15px;
  max-height: calc(85vh - 120px);
}

/* Estilos específicos para mobile para garantir a visualização adequada do modal */
@media (max-width: 576px) {
  .custom-modal-content {
    width: 100%;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
}
</style>
